/**
 * Bulk Action Service
 * Handles bulk operations on invoices
 */

/**
 * Validate bulk selection
 * @param {Array} invoiceIds - Array of invoice IDs
 * @param {number} maxLimit - Maximum selection limit
 * @returns {Object} Validation result
 */
export const validateBulkSelection = (invoiceIds, maxLimit = 100) => {
  if (!invoiceIds || !Array.isArray(invoiceIds) || invoiceIds.length === 0) {
    return {
      valid: false,
      error: 'No invoices selected',
      count: 0,
    };
  }

  const result = {
    valid: true,
    count: invoiceIds.length,
  };

  if (invoiceIds.length > maxLimit) {
    result.warning = `Selection exceeds recommended limit of ${maxLimit}. This may take longer to process.`;
  }

  return result;
};

/**
 * Filter invoices that can be deleted (draft only)
 * @param {Array} invoices - Array of invoice objects
 * @returns {Object} Deletable and skipped invoices
 */
export const filterDeletableInvoices = (invoices) => {
  const deletable = [];
  const skipped = [];

  invoices.forEach((invoice) => {
    if (invoice.status === 'draft') {
      deletable.push(invoice);
    } else {
      skipped.push({
        invoice,
        reason: `Cannot delete invoice with status '${invoice.status}'`,
      });
    }
  });

  return { deletable, skipped };
};

/**
 * Prepare data for bulk email sending
 * @param {Array} invoices - Array of invoice objects
 * @param {Array} clients - Array of client objects
 * @returns {Array} Send data for each invoice
 */
export const prepareBulkSendData = (invoices, clients) => {
  const clientMap = new Map(clients.map((c) => [c.id, c]));

  return invoices
    .map((invoice) => {
      const client = clientMap.get(invoice.clientId);
      if (!client?.email) {
        return null;
      }

      return {
        invoiceId: invoice.id,
        invoiceNumber: invoice.invoiceNumber,
        email: client.email,
        clientName: client.name || invoice.clientName,
        amount: invoice.totalAmount,
        currency: invoice.currency,
        dueDate: invoice.dueDate,
      };
    })
    .filter(Boolean);
};

/**
 * Format a date as YYYY-MM-DD
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date
 */
const formatDate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

/**
 * Capitalize first letter
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Format invoice for CSV export
 * @param {Object} invoice - Invoice object
 * @returns {Object} Formatted row for CSV
 */
export const formatInvoiceForCsv = (invoice) => {
  return {
    'Invoice Number': invoice.invoiceNumber || '',
    'Client': invoice.clientName || '',
    'Amount': (invoice.totalAmount || 0).toFixed(2),
    'Currency': invoice.currency || 'USD',
    'Status': capitalize(invoice.status),
    'Issue Date': formatDate(invoice.issueDate),
    'Due Date': formatDate(invoice.dueDate),
    'Paid Date': formatDate(invoice.paidAt),
  };
};

/**
 * Prepare invoices for bulk export
 * @param {Array} invoices - Array of invoice objects
 * @returns {Array} Formatted rows for export
 */
export const prepareBulkExportData = (invoices) => {
  return invoices.map(formatInvoiceForCsv);
};

/**
 * Aggregate results from bulk operation
 * @param {Array} results - Array of operation results
 * @returns {Object} Aggregated summary
 */
export const aggregateBulkResults = (results) => {
  if (!results || results.length === 0) {
    return {
      totalProcessed: 0,
      successCount: 0,
      failureCount: 0,
      successRate: 0,
      errors: [],
    };
  }

  const successCount = results.filter((r) => r.success).length;
  const failureCount = results.length - successCount;
  const errors = results
    .filter((r) => !r.success && r.error)
    .map((r) => `${r.invoiceId}: ${r.error}`);

  return {
    totalProcessed: results.length,
    successCount,
    failureCount,
    successRate: Math.round((successCount / results.length) * 100),
    errors,
  };
};

/**
 * Generate CSV content from data
 * @param {Array} data - Array of row objects
 * @returns {string} CSV content
 */
export const generateCsvContent = (data) => {
  if (!data || data.length === 0) {
    return '';
  }

  const headers = Object.keys(data[0]);
  const headerRow = headers.join(',');

  const dataRows = data.map((row) => {
    return headers
      .map((header) => {
        const value = String(row[header] || '');
        // Escape quotes and wrap in quotes if contains comma or quote
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
      .join(',');
  });

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Get summary of invoices to be affected by bulk action
 * @param {Array} invoices - Array of invoice objects
 * @returns {Object} Summary statistics
 */
export const getBulkActionSummary = (invoices) => {
  const summary = {
    totalCount: invoices.length,
    totalAmount: 0,
    byStatus: {},
    byCurrency: {},
  };

  invoices.forEach((invoice) => {
    summary.totalAmount += invoice.totalAmount || 0;

    // Count by status
    const status = invoice.status || 'unknown';
    summary.byStatus[status] = (summary.byStatus[status] || 0) + 1;

    // Count by currency
    const currency = invoice.currency || 'USD';
    summary.byCurrency[currency] = (summary.byCurrency[currency] || 0) + 1;
  });

  return summary;
};

/**
 * Split invoices into batches for processing
 * @param {Array} invoices - Array of invoice objects
 * @param {number} batchSize - Size of each batch
 * @returns {Array} Array of batches
 */
export const splitIntoBatches = (invoices, batchSize = 10) => {
  const batches = [];
  for (let i = 0; i < invoices.length; i += batchSize) {
    batches.push(invoices.slice(i, i + batchSize));
  }
  return batches;
};
